import {
  Button,
  Header,
  SpaceBetween,
  Tabs,
} from '@cloudscape-design/components';
import useProjects from 'hooks/use-projects';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import BagsTab from './components/BagsTab';
import ByItemTab from './components/ByItemTab';
import NewLotModal from './components/NewLotModal';

const ALL_PROJECTS = { label: 'All projects', value: null };

export default function LotsPage() {
  const navigate = useNavigate();
  // Every filter lives in the URL so part pages and lot pages can
  // deep-link "all lots of this item" and any filtered view is shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const projectDocId = searchParams.get('projectDocId');
  // A part filter is meaningless without its project — ignore an orphan.
  const pPartDocId = projectDocId ? searchParams.get('pPartDocId') : null;
  const kind = searchParams.get('kind') || 'all';
  const status = searchParams.get('status') || 'all';
  const filterText = searchParams.get('code') || '';
  const view = searchParams.get('view') || 'bags';
  const includeEmpty = searchParams.get('includeEmpty') === '1';
  const [openNewLotModal, setOpenNewLotModal] = useState(false);

  // replace: keystroke-level updates (the code text) rewrite the current
  // history entry; discrete filter picks push one, so Back steps them.
  const updateFilters = (patch, { replace = false } = {}) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([key, value]) => {
          if (value && value !== 'all') next.set(key, value);
          else next.delete(key);
        });
        return next;
      },
      { replace },
    );
  };

  // One definition of "all the lots-page filters" — each tab's Clear
  // button shows based on its own visible filters but resets everything,
  // so no hidden state from the other tab survives a clear.
  const clearFilters = () => updateFilters({
    code: null,
    projectDocId: null,
    pPartDocId: null,
    kind: null,
    status: null,
    includeEmpty: null,
  });

  const { projectOptions, isLoading: projectsLoading } = useProjects({ isInternal: true });
  const projectFilterOptions = useMemo(
    // Re-keyed on value (the doc ID) so both selects share one option shape.
    () => [ALL_PROJECTS, ...projectOptions.map(({ label, id }) => ({ label, value: id }))],
    [projectOptions],
  );
  const selectedProjectOption = projectDocId
    ? (projectFilterOptions.find((option) => option.value === projectDocId)
      || { label: projectDocId, value: projectDocId })
    : ALL_PROJECTS;

  return (
    <SpaceBetween size="s">
      <Header
        variant="awsui-h1-sticky"
        actions={(
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => navigate('/lots/trace')}>
              Trace
            </Button>
            <Button onClick={() => navigate('/lots/assemble')}>
              Assemble lot
            </Button>
            <Button
              iconName="add-plus"
              variant="primary"
              onClick={() => setOpenNewLotModal(true)}
            >
              New lot
            </Button>
          </SpaceBetween>
        )}
      >
        Lots
      </Header>

      {/* Tab state in the URL, like the parts page's List | Flow. */}
      <Tabs
        activeTabId={view}
        onChange={({ detail }) => updateFilters({
          view: detail.activeTabId === 'bags' ? null : detail.activeTabId,
        })}
        tabs={[
          {
            id: 'bags',
            label: 'Bags',
            content: (
              <BagsTab
                projectDocId={projectDocId}
                pPartDocId={pPartDocId}
                kind={kind}
                status={status}
                filterText={filterText}
                onFiltersChange={updateFilters}
                onClearFilters={clearFilters}
                projectFilterOptions={projectFilterOptions}
                selectedProjectOption={selectedProjectOption}
                projectsLoading={projectsLoading}
              />
            ),
          },
          {
            id: 'items',
            label: 'By item',
            content: (
              <ByItemTab
                projectDocId={projectDocId}
                kind={kind}
                includeEmpty={includeEmpty}
                onFiltersChange={updateFilters}
                onClearFilters={clearFilters}
                projectFilterOptions={projectFilterOptions}
                selectedProjectOption={selectedProjectOption}
                projectsLoading={projectsLoading}
              />
            ),
          },
        ]}
      />

      {openNewLotModal && (
        <NewLotModal
          open={openNewLotModal}
          onClose={() => setOpenNewLotModal(false)}
          // Land on the new lot's detail page, where the Print label button is.
          onSuccess={({ id }) => navigate(`/lots/${id}`)}
        />
      )}
    </SpaceBetween>
  );
}
